import FormGroupWithTooltip from '@/components/common/FormGroup/FormGroupWithTooltip';
import IsrcInput from '@/components/common/IsrcInput';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrackEditViewTemplateProps } from '../TrackEditViewTemplate';
import ReadOnlyField from '@/components/common/ReadOnlyField/ReadOnlyField';
import { VALIDATION_STATE_WARNING } from '@/utils/validationErrors';
import { AlbumState } from '@/data/models/albumState';
import { allowsIdentifierAssignment } from '@/utils/productStateUtils';

type Props = Pick<
  TrackEditViewTemplateProps,
  | 'trackData'
  | 'handleInput'
  | 'fieldHasErrors'
  | 'fieldValidationState'
  | 'findErrorMessage'
  | 'isMultipleTrack'
> & {
  readonly?: boolean;
  disabled?: boolean;
  displayValueOnly?: boolean;
  albumState?: AlbumState;
};

export default function TrackIsrcFormGroup({
  isMultipleTrack,
  trackData,
  readonly,
  disabled,
  displayValueOnly,
  albumState,
  handleInput,
  fieldHasErrors,
  fieldValidationState,
  findErrorMessage,
}: Props) {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const [isEdited, setIsEdited] = useState(false);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsFocused(false);
      setIsEdited(false);
    }
  };

  const validationState = fieldValidationState?.('Isrc');
  const showError = fieldHasErrors('Isrc') && !isEdited;
  const modeSelectorDisabled = !allowsIdentifierAssignment(albumState);
  const errorMessageClassName =
    validationState === VALIDATION_STATE_WARNING
      ? 'col-sm-3 popUp-container-warning'
      : 'col-sm-3 popUp-container';
  const labelTitle = t('track.isrc');

  return (
    <>
      {!readonly ? (
        <FormGroupWithTooltip
          tooltipKey="tooltip.track.isrc"
          hasErrors={!isMultipleTrack && !disabled ? showError : false}
        >
          {!isMultipleTrack ? (
            <>
              <label htmlFor="isrc" className="col-sm-3 form-label">
                {labelTitle}
              </label>
              <div
                className="col-sm-6"
                onFocus={handleFocus}
                onBlur={handleBlur}
              >
                <IsrcInput
                  value={trackData.isrc}
                  onChange={(value) => {
                    setIsEdited(true);
                    handleInput('isrc', value);
                  }}
                  hasError={showError}
                  validationState={validationState}
                  disabled={disabled}
                  modeSelectorDisabled={modeSelectorDisabled}
                  hideModeSelector={displayValueOnly}
                />
              </div>
              {showError && isFocused && (
                <div className={errorMessageClassName}>
                  {findErrorMessage('Isrc')}
                </div>
              )}
            </>
          ) : null}
        </FormGroupWithTooltip>
      ) : (
        <ReadOnlyField
          id={'isrc'}
          labelTitle={labelTitle}
          value={trackData.isrc}
        ></ReadOnlyField>
      )}
    </>
  );
}
